import { Ng2StateDeclaration } from '@uirouter/angular';
import { Rejection, StateService, Transition } from '@uirouter/core';


import { StoreAction } from 'store/actions';
import { HttpError } from 'services/http/error';
import { Cloudinary } from 'services/cloudinary';
import { CimaCore, SessionStorage, XmStore, XmStoreUtil } from 'core/services';

import { LineBannerInfo } from 'store/line-banner/models';
import { UsageSummary } from 'store/usage/models';
import { TransactionCollection } from 'store/transaction/models';
import { Datasaver } from 'store/line/models/datasaver';
import { Account, AllowedLines, ProfileLines, User } from 'store/user/models';
import { InternationalRate } from 'store/international-rates/models';
import { AddonCollections } from 'store/addons/addon-collections';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import {
    CmsAccountDataPlanCollection,
    CmsActivity,
    CmsAddonDetails,
    CmsAddonErrors,
    CmsAddonPlans,
    CmsCore,
    CmsCountryCodingMapping,
    CmsDataDetail,
    CmsInternationalCalling,
    CmsInternationalTravel,
    CmsLineDataPlanCollection,
    CmsPlanSwitch,
    CmsUsageDetail
} from 'store/cms/models';

import { ActivityComponent } from './landing/landing';
import { LineDetailComponent } from './line-detail/detail';
import { PlanSwitchComponent } from './line-detail/plan-switch/landing';
import { PlanSwitchBucketPickerComponent } from './line-detail/plan-switch/bucket-picker/bucket-picker';
import { PlanSwitchReviewComponent } from './line-detail/plan-switch/review/review';
import { PlanSwitchResultComponent } from './line-detail/plan-switch/result/result';
import { PlanSwitchInProgressComponent } from 'my-account/activity/line-detail/plan-switch/in-progress/in-progress';
import { PlanSwitchScheduledInProgressComponent } from './line-detail/plan-switch/scheduled-in-progress/scheduled-in-progress';
import { PlanSwitchIneligibleOverageComponent } from './line-detail/plan-switch/ineligible-overage/ineligible-overage';
import { XmCallFromUS } from './line-detail/call-from-us/call-from-us';
import { XmInternationalTravel } from './line-detail/international-travel/international-travel';
import { XmInternationalCalling } from './line-detail/international-calling/international-calling';
import { XmTravelRateSearch } from './line-detail/travel-rate-search/travel-rate-search';
import { XmUsageDetailComponent } from '../shared/usage-detail/usage-detail';
import { BREADCRUMB_TOKEN, CONFIG_TOKEN, ParamType, StorageToken } from 'core/constants';
import { AddonRouteType, AddonType, BreadcrumbLabel, USAGE_SUMMARY_STATE, UserErrorType } from 'my-account/constants';
import { CmsCallFromUS } from 'store/cms/models/call-from-us';
import { CmsTravelRates } from 'store/cms/models/travel-rates';
import { LineTransferBySessionId } from 'store/line/models/line-transfer/line-transfer-by-session-id';
import { IXMOptions } from 'core/interfaces';
import { XmPlanSwitchUnlimitedTierCancelDowngrade } from './line-detail/plan-switch/unlimited-tier-cancel-downgrade/unlimited-tier-cancel-downgrade';
import { PlanSwitchUnlimitedTiersReviewComponent } from './line-detail/plan-switch/unl-tiers-review/unl-tiers-review';
import { LoggerService } from 'services/http/logger';
import { CustomerGuard } from 'src/xm/route-guards';
import { DiscountPromoCollection } from 'store/promotions/discount-promotion/models';
import { BroadbandLabels } from 'store/broadband-label/models';
import { planEligibilityDetails } from 'store/plan-eligibility/models';
import { XmUnlTargetPlanSwitchReviewComponent } from './line-detail/plan-data-options/unl-plan-review/unl-plan-review';
import { XmBtgTargetPlanSwitchReviewComponent } from './line-detail/plan-data-options/btg-plan-review/btg-plan-review';
import { XmAddonReview } from './new-addon-flow/addon-review/addon-review';
import { XmAddonPending } from './new-addon-flow/addon-pending/addon-pending';
import { XmAddonProgress } from './new-addon-flow/addon-progress/addon-progress';
import { XmAddonSuccess } from './new-addon-flow/addon-success/addon-success';
import { XmAddonSummary } from './new-addon-flow/addon-summary/addon-summary';

import { XmAddOnFailure } from './new-addon-flow/addon-failure/addon-failure';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { XmPlanSwitchReviewOrder } from './line-detail/plan-switch/review-order/plan-switch-review-order';
import { XmcConsentPageComponent } from './line-detail/plan-switch/xmc-consent-page/xmc-consent-page';
import { GetCmsLevelMoveStart } from 'my-account/settings/level-move/routes';
import { XmcPlanSwitchService } from 'my-account/activity/shared/services/xmc-plan-switch.service';
import { XmSwitchLocationsComponent } from './switch-locations/switch-locations';
import { GetCbmLocations } from 'my-account/routes';

export function GetBannerPageData(xmStore: XmStore): Promise<CmsActivity> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsActivity>(StoreAction.GET_ACTIVITY, CmsActivity));
}

export function GetAccountDataPlanPageData(xmStore: XmStore): Promise<CmsAccountDataPlanCollection> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsAccountDataPlanCollection>(StoreAction.GET_ACCOUNT_DATA_PLAN, CmsAccountDataPlanCollection));
}

export function GetLineDataPlanPageData(xmStore: XmStore): Promise<CmsLineDataPlanCollection> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLineDataPlanCollection>(StoreAction.GET_LINE_DATA_PLAN, CmsLineDataPlanCollection));
}

export function GetDataDetailPageData(xmStore: XmStore): Promise<CmsDataDetail> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsDataDetail>(StoreAction.GET_DATA_DETAIL, CmsDataDetail));
}

export function GetAddonDetails(xmStore: XmStore): Promise<CmsAddonDetails> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsAddonDetails>(StoreAction.GET_ADDONS_DETAILS, CmsAddonDetails));
}

export function GetAllowedLines(xmStore: XmStore): Promise<AllowedLines> {
    return XmStoreUtil.defaultCatch(xmStore.findChildPromise<AllowedLines, User>(StoreAction.GET_ALLOWED_LINES, 'allowedLines', AllowedLines, User));
}

export function GetPlanEligibilityDetails(cimaCore: CimaCore, transition: Transition, xmStore: XmStore, account: Account): Promise<planEligibilityDetails | void> {
    const state: StateService = transition.router.stateService;
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);

    let action: string;

    if (config.ENABLE_CBM_GEMINI && cimaCore.isBusinessUser && account.isNoLevel) {
        action = StoreAction.GET_CBM_PLAN_ELIGIBILITY;
    } else if (account.isSegmentSix && !cimaCore.isBusinessUser) {
        action = StoreAction.GET_PLAN_EIGHT_ELIGIBILITY;
    } else if (account.isSegmentFive || account.isSegmentSix) {
        action = StoreAction.GET_PLAN_ELIGIBILITY;
    } else {
        return Promise.resolve();
    }

    return XmStoreUtil.defaultCatch(xmStore.findPromise<planEligibilityDetails>(action, planEligibilityDetails, {
        params: { lineId: transition.params().line_id },
        filters: { lineId: transition.params().line_id }
    }).catch(() => {
        transition.abort();
        state.go('error');
    }));
}

export function GetAccountSummary(xmStore: XmStore, transition: Transition, account: Account): Promise<AccountSummary|void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
    const cimaCore: CimaCore = transition.injector().get(CimaCore);
 
    let action: string;

    if (config.ENABLE_CBM_GEMINI && cimaCore.isBusinessUser && account.isNoLevel) {
        action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
    } else if (account.isSegmentSix && !cimaCore.isBusinessUser) {
        action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
    } else if (account.isSegmentFive || account.isSegmentSix) {
        action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
    } else {
        action = StoreAction.GET_ACCOUNT_SUMMARY;
    }

    return xmStore.findPromise<AccountSummary>(action, AccountSummary).catch((error: HttpError) => {
        if (!transition.params().hasChangePending && error.status === 421) {
            transition.abort();
            transition.router.stateService.go('account.activity.landing', { hasChangePending: true });

            return Promise.reject(Rejection.aborted('421 Error'));
        } else if (!transition.params().hasAPIError && error.status === 500) {
            transition.abort();
            transition.router.stateService.go('account.activity.landing', { hasAPIError: true });

            return Promise.reject(Rejection.aborted('500 Error'));
        } else if (!transition.params().hasAPIError && error.status >= 400) {
            transition.abort();

            transition.router.stateService.go('non-xfinity-customer', { error_type: UserErrorType.NOT_USER });
        }

        return Promise.resolve();
    });
}

export function GetDatasaverStatus(transition: Transition, xmStore: XmStore, account: Account): Promise<Datasaver|void> {
    if (transition.to().name.includes('plan-switch')) {
        return Promise.resolve();
    }


    if ((account.isMember || account.isViewer)) {
        return Promise.resolve();
    }

    return XmStoreUtil.defaultCatch(xmStore.findPromise<Datasaver>(StoreAction.GET_DATASAVER_STATUS, Datasaver,
        {
            params: { lineKey: transition.params().line_id }
        }));
}

export function GetLineTransfers(transition: Transition, xmStore: XmStore): Promise<LineTransferBySessionId | void> {
    const lineId: string = transition.params()?.line_id;

    return xmStore.findPromise<LineBannerInfo>(
        StoreAction.GET_LINE_BANNER_INFO,
        LineBannerInfo,
        {
            params: { lineId },
            filters: { lineId }
        }
    ).then((bannerInfo: LineBannerInfo): Promise<LineTransferBySessionId | void> => {
        if (bannerInfo?.isLineTransferPendingBanner && bannerInfo.ctaButton?.orderKey) {
            return XmStoreUtil.defaultCatch(
                xmStore.findPromise<LineTransferBySessionId>(
                    StoreAction.GET_LINE_TRANSFER_BY_SESSION_ID,
                    LineTransferBySessionId,
                    {
                        filters: { transferSessionId: bannerInfo.ctaButton.orderKey },
                        params: { transferSessionId: bannerInfo.ctaButton.orderKey }
                    }
                )
            );
        }

        return Promise.resolve();
    });
}

export function GetAddons(transition: Transition, xmStore: XmStore): Promise<AddonCollections|void> {
    if (transition.to().name.includes('plan-switch')) {
        return Promise.resolve();
    }

    if (!transition.params().line_id) {
        return Promise.resolve(undefined);
    }

    return XmStoreUtil.defaultCatch(xmStore.findPromise<AddonCollections>(StoreAction.GET_ADDONS_COLLECTION, AddonCollections,
        {
            filters: { lineId: transition.params().line_id },
            params: { lineId: transition.params().line_id }
        })
    );
}

export function GetAddonCollections(transition: Transition): Promise<AddonCollections | void> {

    const xmStore: XmStore = transition.injector().get(XmStore);
    const state: StateService = transition.router.stateService;

    if (!transition.params().line_id) {
        return Promise.resolve(undefined);
    }

    return XmStoreUtil.defaultCatch(xmStore.findPromise<AddonCollections>(StoreAction.GET_ADDONS_COLLECTION, AddonCollections,
        {
            filters: { lineId: transition.params().line_id },
            params: { lineId: transition.params().line_id }
        }).catch(() => {
        transition.abort();
        state.go('error');
    }));
}

export function GetAccountInfo(xmStore: XmStore): Promise<Account | HttpError> {
    return XmStoreUtil.defaultCatch(xmStore.findChildPromise<Account, User>(StoreAction.GET_ACCOUNT_INFO, 'account',
        Account, User).catch((error: HttpError) => Promise.resolve(error)));
}

export function GetTransactions(xmStore: XmStore): Promise<TransactionCollection> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<TransactionCollection>(StoreAction.GET_TRANSACTIONS_BY_PARAMS, TransactionCollection));
}

export function GetProfileLines(xmStore: XmStore, account: Account): Promise<ProfileLines | void> {

    if (account?.isPrimary) {
    
        return XmStoreUtil.defaultCatch(xmStore.findPromise<ProfileLines>(StoreAction.GET_PROFILE_LINES, ProfileLines));
    
    } else {
        return Promise.resolve();
    }

}

export function GetUsageSummary(transition: Transition, xmStore: XmStore): Promise<UsageSummary|void> {
    if (transition.to().name.includes('plan-switch')) {
        return Promise.resolve();
    }

    // usage api with summary version
    return XmStoreUtil.defaultCatch(xmStore.findPromise<UsageSummary>(StoreAction.GET_USAGE_SUMMARY, UsageSummary, {
        filters: { 'lineId.key' : transition.params().line_id },
        params: { lineId: transition.params().line_id }
    }));
}

export function GetActivityBreadcrumb(): AccountBreadcrumb {
    return {
        label: 'Activity',
        stateName: 'account.activity.landing'
    };
}

export function GetSwitchLocationsBreadcrumb(): AccountBreadcrumb {
    return {
        label: 'Switch Locations',
        stateName: 'account.activity.switch-locations'
    };
}

export function CheckSwitchLocationsAccess(cimaCore: CimaCore, transition: Transition): Promise<void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
    const sessionStorage: SessionStorage = transition.injector().get(SessionStorage);
    const isOrionTenant: boolean = sessionStorage.get<string>(StorageToken.X_TENANT_ID) === 'ORION';
    const enableCbmAccountSelector: boolean = config?.ENABLE_CBM_ACCOUNT_SELECTOR && !isOrionTenant;
    const enableOrionAccountSelector: boolean = config?.ENABLE_ORION_ACCOUNT_SELECTOR && isOrionTenant;

    if (cimaCore.isBusinessUser && (enableCbmAccountSelector || enableOrionAccountSelector)) {
        return Promise.resolve();
    }

    transition.abort();
    transition.router.stateService.go('account.activity.landing');

    return Promise.reject(Rejection.aborted('switch-locations access denied'));
}

export function GetCallFromUSBreadcrumb(pageData: CmsCallFromUS): AccountBreadcrumb {
    return {
        label: pageData.title,
        stateName: 'account.activity.details.call-from-us'
    };
}

export function GetPlanSwitchBreadcrumb(transition: Transition, cmsLineDataPlanCollection: CmsLineDataPlanCollection): AccountBreadcrumb {
    if (transition.params().plan_code) {
        const planCode: string = decodeURIComponent(transition.params().plan_code);
        const selectedPlanData: CmsDataPlanDetails = cmsLineDataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(planCode));

        if (ShouldShowUnlBreadcrumbLabel(transition)) {
            return {
                label: BreadcrumbLabel.UNLIMITED,
                stateName: 'account.activity.details.plan-switch'
            };
        } else {
            return {
                label: selectedPlanData.planName,
                stateName: 'account.activity.details.plan-switch'
            };
        }
    } else {
        return {
            label: BreadcrumbLabel.BY_THE_GIG,
            stateName: 'account.activity.details.plan-switch'
        };
    }
}

export function ShouldShowUnlBreadcrumbLabel(transition: Transition): boolean {
    return transition.to().name === 'account.activity.details.plan-switch.unl-tiers-review' || transition.to().name === 'account.activity.details.plan-switch.xm-unl-target-plan-review' ||
        transition.to().name === 'account.activity.details.plan-switch.unl-tiers-cancel-downgrade';
}

export function GetActivityDetailBreadcrumb(transition: Transition, accountSummary: AccountSummary, showBackToBreadCrumb: AccountBreadcrumb): AccountBreadcrumb {
    if (!accountSummary) {
        return;    
    } else if (showBackToBreadCrumb) {
        return showBackToBreadCrumb;
    }

    const lineSummary: LineSummary = accountSummary.getLineByKey(transition.params().line_id);

    return {
        label: lineSummary.displayName || '',
        stateName: 'account.activity.details',
        stateParams: {
            line_id: transition.params().line_id
        }
    };
}

export function GetAccountActivityMetadata(xmStore: XmStore, logger: LoggerService): Promise<MetaDataDefault> {
    logger.pageInfo('My Account Activity');

    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsCore>(StoreAction.GET_CORE, CmsCore)
        .then((response: CmsCore) => response.metaData.myAccount.activity));
}

export function GetPlanSwitchPageData(xmStore: XmStore): Promise<CmsPlanSwitch> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsPlanSwitch>(StoreAction.GET_PLAN_SWITCH, CmsPlanSwitch));
}

export function GetCmsUsageDetail(xmStore: XmStore): Promise<CmsUsageDetail> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsUsageDetail>(StoreAction.GET_CMS_USAGE_DETAIL, CmsUsageDetail));
}

export function GetUsageDetailBreadcrumb(transition: Transition, cmsData: CmsUsageDetail): AccountBreadcrumb {
    if (cmsData) {
        const heading: string = cmsData.header.replace('[NAME]', transition.params().name);

        return {
            label: heading,
            stateName: 'account.activity.usage',
            stateParams: { statement_line_id: transition.params().statement_line_id }
        };
    }
}

export function GetCmsCountryCodeMapping(xmStore: XmStore): Promise<CmsCountryCodingMapping> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsCountryCodingMapping>(StoreAction.GET_CMS_COUNTRY_CODE_MAPPING, CmsCountryCodingMapping));
}
    
export function GetCallFromUSPageData(xmStore: XmStore): Promise<CmsCallFromUS> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsCallFromUS>(StoreAction.GET_CMS_CALL_FROM_US, CmsCallFromUS));
}

// eslint-disable-next-line complexity
export function CheckPlanSwitchRoutes(transition: Transition): Promise<void> {
    if ((transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.bucket-picker') ||
    (transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.review') ||
    (transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.unl-tiers-review') ||
    (transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.xm-unl-target-plan-review') ||
    (transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.xm-btg-target-plan-review') ||
    (transition.from().name === 'account.activity.details.plan-switch.bucket-picker' && transition.to().name === 'account.activity.details.plan-switch.review') ||
    (transition.from().name === 'account.activity.details.plan-switch.review' && transition.to().name === 'account.activity.details.plan-switch.in-progress') ||
    (transition.from().name === 'account.activity.details.plan-switch.in-progress' && transition.to().name === 'account.activity.details.plan-switch.pending') ||
    (transition.from().name === 'account.activity.details.plan-switch.in-progress' && transition.to().name === 'account.activity.details.plan-switch.success') ||
    (transition.from().name === 'account.activity.details.plan-switch.in-progress' && transition.to().name === 'account.activity.details.plan-switch.failure') ||
    (transition.from().name === 'account.activity.details.plan-switch.failure' && transition.to().name === 'account.activity.details.plan-switch.in-progress') ||
    (transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.ineligible-overage') ||
    checkUnlimitedTierRoutes(transition) ||
    (!transition.to().name.includes('plan-switch'))
    ) {
        return Promise.resolve();
    }

    const state: StateService = transition.router.stateService;

    transition.abort();
    state.go('account.activity.details', transition.params());

    return Promise.reject(Rejection.aborted('cannot reload'));
}

function checkUnlimitedTierRoutes(transition: Transition): boolean {
    return ((transition.from().name === 'account.activity.details' && transition.to().name === 'account.activity.details.plan-switch.unl-tiers-cancel-downgrade') ||
    (transition.from().name === 'account.activity.details.plan-switch.unl-tiers-cancel-downgrade' && transition.to().name === 'account.activity.details.plan-switch.scheduled-in-progress'));
}

export function GetInternationalRate(xmStore: XmStore, transition: Transition, cimaCore: CimaCore, accountInfo: Account): Promise<InternationalRate | void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);

    if (config.ENABLE_CBM_INTERNATIONAL_RETE_LINK && cimaCore.isBusinessUser) {
        const mnoCode: string = accountInfo && accountInfo.isNoLevel ? 'TMO' : 'VZW';

        return XmStoreUtil.defaultCatch(xmStore.findPromise<InternationalRate>(StoreAction.GET_INTERNATIONAL_RATES, InternationalRate, {
            params: { mnoCode }
        }));
    } else {

        return XmStoreUtil.defaultCatch(xmStore.findPromise<InternationalRate>(StoreAction.GET_INTERNATIONAL_RATES, InternationalRate));
    }

}

export function GetCmsTravelRates(xmStore: XmStore): Promise<CmsTravelRates> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsTravelRates>(StoreAction.GET_CMS_TRAVEL_RATES, CmsTravelRates));
}
 
export function GetCmsInternationalCalling(xmStore: XmStore): Promise<CmsInternationalCalling> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsInternationalCalling>(StoreAction.GET_CMS_INTERNATIONAL_CALLING, CmsInternationalCalling));
}
export function ShowBackToTravelBreadCrumb(transition: Transition, data: CmsTravelRates): AccountBreadcrumb {
    const state: StateService = transition.router.stateService;
    const sessionStorage: SessionStorage = transition.injector().get(SessionStorage);
    const pageData: CmsInternationalTravelRates = data.travelRates;

    // This condition checks if the user navigated from the Usage Summary screen.
    if ((transition.from().name === 'account.activity.usage' || transition.from().name === 'account.billing.usage') && state.params) {
        return {
            label: pageData.backToBreadCrumb.labelToBilling,
            stateName: transition.from().name,
            stateParams: { statement_line_id: state.params.statement_line_id, activity: state.params.activity, startDate: state.params.startDate, endDate: state.params.endDate, name: state.params.name, lineNumber: state.params.lineNumber },
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(pageData.backToBreadCrumb.icon) },
            isBackTo: true
        };
    } else if (sessionStorage.get(USAGE_SUMMARY_STATE)) {
        return {
            label: pageData.backToBreadCrumb.labelToBilling,
            stateName: 'account.billing.landing',
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(pageData.backToBreadCrumb.icon) },
            isBackTo: true
        };
    // send user back to International search call rate or Travel Rate search screen
    } else {
        return {
            label: pageData.backToBreadCrumb.labelToBackScreen,
            stateName: pageData.backToBreadCrumb.defaultState,
            stateParams: transition.params(),
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(pageData.backToBreadCrumb.icon) },
            isBackTo: true
        };
    }
}

export function ShowBackToBreadCrumb(transition: Transition, pageData: CmsInternationalCalling): AccountBreadcrumb {
    const state: StateService = transition.router.stateService;
    const sessionStorage: SessionStorage = transition.injector().get(SessionStorage);

    // This condition is used for if user can came from Usage summary screen or not
    if ((transition.from().name === 'account.activity.usage' || transition.from().name === 'account.billing.usage') && state.params) {
        return {
            label: pageData.backToBreadCrumb.labelToBilling,
            stateName: transition.from().name,
            stateParams: { statement_line_id: state.params.statement_line_id, activity: state.params.activity, startDate: state.params.startDate, endDate: state.params.endDate, name: state.params.name, lineNumber: state.params.lineNumber },
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(pageData.backToBreadCrumb.icon) },
            isBackTo: true
        };
    } else if (sessionStorage.get(USAGE_SUMMARY_STATE)) {
        return {
            label: pageData.backToBreadCrumb.labelToBilling,
            stateName: 'account.billing.landing',
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(pageData.backToBreadCrumb.icon) },
            isBackTo: true
        };
    // send user back to International search call rate or Travel Rate search screen
    } else {
        return {
            label: pageData.backToBreadCrumb.labelToBackScreen,
            stateName: pageData.backToBreadCrumb.defaultState,
            stateParams: transition.params(),
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(pageData.backToBreadCrumb.icon) },
            isBackTo: true
        };
    }
}

export function ClearSession(transition: Transition): void {
    const sessionStorage: SessionStorage = transition.injector().get(SessionStorage);

    sessionStorage.destroy(USAGE_SUMMARY_STATE);
}

export function GetCmsInternationalTravel(xmStore: XmStore): Promise<CmsInternationalTravel> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsInternationalTravel>(StoreAction.GET_CMS_INTERNATIONAL_TRAVEL, CmsInternationalTravel));
}

export function GetInternationalTravelBreadcrumb(pageData: CmsInternationalTravel): AccountBreadcrumb {
    return {
        label: pageData.title,
        stateName: 'account.activity.details.travel'
    };
}

export function GetDiscountPromotions( xmStore: XmStore): Promise<DiscountPromoCollection|void> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection));
}

export function ClearXmcPlanSwitchState(xmcPlanSwitchService: XmcPlanSwitchService): void {
    xmcPlanSwitchService.clearPlanSwitchState();
}

export function GetBroadbandLabel(cimaCore: CimaCore, xmStore: XmStore, account: Account, pageData: CmsDataDetail): Promise<BroadbandLabels | void> {
    if (cimaCore.isBusinessUser && (account.isSegmentFive || account.isNoLevel)) {
        const request: NutritionLabelRequest = {
            lineOfBusiness: 'MOBILE',
            planIds: account.isSegmentFive ? pageData.segmentFiveCBMPlanIds : pageData.cbmNoLevelPlanIds,
            addressInfo: {
                geoCode: account.geoCode,
                state: account.serviceAddress.state
            }
        };

        return XmStoreUtil.defaultCatch(xmStore.persist<BroadbandLabels>(StoreAction.GET_BROADBAND_LABELS, request));
    } else {
        return Promise.resolve();
    }
}

export function GetCmsAddonPlans(xmStore: XmStore): Promise<CmsAddonPlans> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsAddonPlans>(StoreAction.GET_ADDONS_PLANS, CmsAddonPlans));
}

export function GetAddonErrorPageData(xmStore: XmStore): Promise<CmsAddonErrors> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsAddonErrors>(StoreAction.GET_ADDON_ERRORS, CmsAddonErrors));
}

export function GetTargetPlanUnlLegacySwitch(transition: Transition, xmStore: XmStore): Promise<targetPlanSwitchDetails | void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);

    if (config.ENABLE_ICOP_P1_PLANSWITCH) {
        return xmStore.fetchPromise<targetPlanSwitchDetails>(StoreAction.GET_TARGET_PLAN_LEGACY_UNL_SWITCH, {
            params: { lineId: transition.params().line_id }
        }).catch(() => Promise.resolve());
    } else {
        return Promise.resolve();
    }
}

export function GetTargetPlanBtgLegacySwitch(transition: Transition, xmStore: XmStore): Promise<targetPlanSwitchDetails | void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);

    if (config.ENABLE_ICOP_P1_PLANSWITCH) {
        return xmStore.fetchPromise<targetPlanSwitchDetails>(StoreAction.GET_TARGET_PLAN_LEGACY_BTG_SWITCH, {
            params: { lineId: transition.params().line_id }
        }).catch(() => Promise.resolve());
    } else {
        return Promise.resolve();
    }
}

export function GetAddonBreadcrumb(transition: Transition, addonsPlansData: CmsAddonPlans): AccountBreadcrumb {
    let addonType: string;
    if (transition.params().addon_type === AddonRouteType.GTP) {
        addonType = AddonType.GTP;
    } else if (transition.params().addon_type === AddonRouteType.MEX_CAN_PASS) {
        addonType = AddonType.CAN_MEX_UNL_PASS;
    }

    const addonRoutes: string[] = [
        'account.activity.addon.review.progress',
        'account.activity.addon.review.pending',
        'account.activity.addon.review.success',
        'account.activity.addon.review.failure',
        'account.activity.addon.review.summary'
    ];

    const addOnPlanData: CmsAddonPlan = addonsPlansData.plans.find((addon: CmsAddonPlan) => (addon.gtp1B && addon.type === addonType));
    let breadCrumbData: CmsBackToBreadCrumb;
    if (['account.activity.addon.review'].includes(transition.to().name)) {
        breadCrumbData = addOnPlanData.content.reviewData.backToBreadCrumb;
    } else if (addonRoutes.includes(transition.to().name)) {
        breadCrumbData = addOnPlanData.content.summaryData.backToBreadCrumb;
    }


    if (breadCrumbData) {
        return {
            label: breadCrumbData.label,
            stateName: breadCrumbData.stateName,
            stateParams: transition.params(),
            cmsIcon: { mobile: Cloudinary.generateMediaFromCms(breadCrumbData.icon) },
            isBackTo: false
        };
    }
}

export function CheckAddonRoutes(transition: Transition): Promise<boolean> {
    if (['account.activity.addon.review'].includes(transition.to().name)) {
        return Promise.resolve(true);
    }

    const state: StateService = transition.router.stateService;

    transition.abort();
    state.go('account.activity.landing', transition.params());

    return Promise.reject(Rejection.aborted('cannot reload'));
}

export const ACTIVITY_ROUTES: Ng2StateDeclaration[] = [
    {
        abstract: true,
        name: 'account.activity',
        url: 'activity',
        resolve: [
            {
                token: 'metaData',
                deps: [ XmStore, LoggerService, Transition ],
                resolveFn: GetAccountActivityMetadata
            },
            {
                token: BREADCRUMB_TOKEN,
                resolveFn: GetActivityBreadcrumb
            },
            {
                token: 'accountDataPlanPageData',
                deps: [ XmStore ],
                resolveFn: GetAccountDataPlanPageData
            },
            {
                token: 'lineDataPlanPageData',
                deps: [ XmStore ],
                resolveFn: GetLineDataPlanPageData
            }
        ]
    },
    {
        name: 'account.activity.landing',
        url: '?tab',
        data: {
            screenName: 'activity-landing',
            subCategory1: 'my-account'
        },
        params: {
            hasChangePending: {
                type: ParamType.any
            },
            hasAPIError: {
                type: ParamType.any
            },
            tab: {
                type: ParamType.any
            }
        },
        resolve: [
            {
                token: 'accountInfo',
                deps: [ XmStore ],
                resolveFn: GetAccountInfo
            },
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetBannerPageData
            },
            {
                token: 'allowedLines',
                deps: [ XmStore ],
                resolveFn: GetAllowedLines
            },
            {
                token: 'accountSummary',
                deps: [ XmStore, Transition, 'accountInfo' ],
                resolveFn: GetAccountSummary
            },
            {
                token: 'discountPromotion',
                deps: [ XmStore ],
                resolveFn: GetDiscountPromotions
            },
            {
                token: 'addonsPlansData',
                deps: [XmStore],
                resolveFn: GetCmsAddonPlans
            },
            {
                token: 'profileLines',
                deps: [ XmStore, 'accountInfo' ],
                resolveFn: GetProfileLines
            }
        ],
        views: {
            'content@account': {
                component: ActivityComponent
            }
        }
    },
    {
        name: 'account.activity.details',
        url: '/data-details/:line_id/',
        data: {
            screenName: 'activity-line-details',
            subCategory1: 'my-account'
        },
        params: {
            line_id: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'accountSummary',
                deps: [ XmStore, Transition, 'accountInfo' ],
                resolveFn: GetAccountSummary
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetBannerPageData
            },
            {
                token: 'dataDetailPageData',
                deps: [ XmStore ],
                resolveFn: GetDataDetailPageData
            },
            {
                token: 'addonsDetailPageData',
                deps: [ XmStore ],
                resolveFn: GetAddonDetails
            },
            {
                token: 'planPageData',
                deps: [ XmStore ],
                resolveFn: GetAccountDataPlanPageData
            },
            {
                token: 'datasaver',
                deps: [ Transition, XmStore, 'accountInfo' ],
                resolveFn: GetDatasaverStatus
            },
            {
                token: 'usage',
                deps: [ Transition, XmStore, 'accountSummary' ],
                resolveFn: GetUsageSummary
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ Transition, 'accountSummary' ],
                resolveFn: GetActivityDetailBreadcrumb
            },
            {
                token: 'lineTransfer',
                deps: [ Transition, XmStore ],
                resolveFn: GetLineTransfers
            },
            {
                token: 'cbmBroadbandLabel',
                deps: [ CimaCore, XmStore, 'accountInfo', 'dataDetailPageData' ],
                resolveFn: GetBroadbandLabel
            },
            {
                token: 'profileLines',
                deps: [ XmStore, 'accountInfo' ],
                resolveFn: GetProfileLines
            },
            {
                token: 'cmsLevelMoveStart',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveStart
            },
            {
                token: 'clearXmcPlanSwitchState',
                deps: [ XmcPlanSwitchService ],
                resolveFn: ClearXmcPlanSwitchState
            }
        ],
        views: {
            'content@account': {
                component: LineDetailComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch',
        url: 'plan-switch',
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'lineDataPlanPageData',
                deps: [ XmStore ],
                resolveFn: GetLineDataPlanPageData
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ Transition, 'lineDataPlanPageData' ],
                resolveFn: GetPlanSwitchBreadcrumb
            },
            {
                token: 'checkData',
                deps: [ Transition ],
                resolveFn: CheckPlanSwitchRoutes
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.bucket-picker',
        url: '/bucket-picker?plan_code',
        data: {
            screenName: 'line-plan-switch-bucket-picker',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            },
            {
                token: 'targetPlanBTGLegacySwitch',
                deps: [ Transition, XmStore, 'accountInfo' ],
                resolveFn: GetTargetPlanBtgLegacySwitch
            },
            {
                token: 'accountInfo',
                deps: [XmStore],
                resolveFn: GetAccountInfo
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchBucketPickerComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.review',
        url: '/review?plan_code',
        data: {
            screenName: 'line-plan-switch-review',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            },
            {
                token: 'targetPlanBTGLegacySwitch',
                deps: [ Transition, XmStore, 'accountInfo' ],
                resolveFn: GetTargetPlanBtgLegacySwitch
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchReviewComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.unl-tiers-review',
        url:'/unl-tiers-review?plan_code',
        data: {
            screenName: 'line-plan-switch-unl-tiers-review',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            },
            {
                token: 'targetPlanUnlLegacySwitch',
                deps: [ Transition, XmStore, 'accountInfo' ],
                resolveFn: GetTargetPlanUnlLegacySwitch
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchUnlimitedTiersReviewComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.xm-unl-target-plan-review',
        url:'',
        data: {
            screenName: 'xm-unl-target-plan-review',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            },
            {
                token: 'clearXmcPlanSwitchState',
                deps: [ XmcPlanSwitchService ],
                resolveFn: ClearXmcPlanSwitchState
            }
        ],
        views: {
            'content@account': {
                component: XmUnlTargetPlanSwitchReviewComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.xm-btg-target-plan-review',
        url:'',
        data: {
            screenName: 'xm-btg-target-plan-review',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account': {
                component: XmBtgTargetPlanSwitchReviewComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.in-progress',
        url: '/in-progress?plan_code',
        data: {
            screenName: 'line-plan-switch-inprogress',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchInProgressComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.scheduled-in-progress',
        url: '/scheduled-in-progress?plan_code&scheduled_date',
        data: {
            screenName: 'line-plan-switch-scheduled-inprogress',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            },
            scheduled_date: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            },
            {
                token: 'planEligibilityDetails',
                deps: [ CimaCore, Transition, XmStore, 'accountInfo' ],
                resolveFn: GetPlanEligibilityDetails
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchScheduledInProgressComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.pending',
        url: '/pending?plan_code&status',
        data: {
            screenName: 'line-plan-switch-pending',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            },
            status: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account.activity.details.plan-switch': {
                component: PlanSwitchResultComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.success',
        url: '/success?plan_code&status',
        data: {
            screenName: 'line-plan-switch-success',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            },
            status: {
                type: ParamType.encoded
            },
            isCancelScheduledPlan: {
                type: ParamType.any
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account.activity.details.plan-switch': {
                component: PlanSwitchResultComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.failure',
        url: '/failure?plan_code&status',
        data: {
            screenName: 'line-plan-switch-failure',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            },
            status: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account.activity.details.plan-switch': {
                component: PlanSwitchResultComponent
            }
        }
    },
    {
        name: 'account.activity.usage',
        url: '/usage/:activity/:statement_line_id?startDate&endDate&name&lineNumber',
        data: {
            screenName: 'billing-line-usage',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'cmsUsageDetail',
                deps: [ XmStore ],
                resolveFn: GetCmsUsageDetail
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ Transition, 'cmsUsageDetail' ],
                resolveFn: GetUsageDetailBreadcrumb
            },
            {     
                token: 'cmsCountryCodeMapping',
                deps: [ XmStore ],
                resolveFn: GetCmsCountryCodeMapping
            }
        ],
        views: {
            'content@account': {
                component: XmUsageDetailComponent
            }
        },
        params: {
            statement_line_id: {
                type: ParamType.encoded
            },
            name: {
                type: ParamType.encoded,
                dynamic: true
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.ineligible-overage',
        url: '/ineligible-overage',
        data: {
            screenName: 'line-plan-switch-ineligible-overage',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account': {
                component: PlanSwitchIneligibleOverageComponent
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.unl-tiers-cancel-downgrade',
        url:'/cancel-scheduled-downgrade?plan_code',
        data: {
            screenName: 'line-plan-switch-unl-tiers-cancel-downgrade',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            },
            {
                token: 'targetPlanUnlLegacySwitch',
                deps: [ Transition, XmStore, 'accountInfo' ],
                resolveFn: GetTargetPlanUnlLegacySwitch
            }
        ],
        views: {
            'content@account': {
                component: XmPlanSwitchUnlimitedTierCancelDowngrade
            }
        }
    },
    {
        name: 'account.activity.details.call-from-us',
        url: 'call-from-us',
        data: {
            screenName: 'line-call-from-us',
            subCategory1: 'my-account'
        },
        params: {
            line_id: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'accountSummary',
                deps: [ XmStore, Transition, 'accountInfo' ],
                resolveFn: GetAccountSummary
            },
            {
                token: 'addonCollections',
                deps: [ Transition ],
                resolveFn: GetAddonCollections
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCallFromUSPageData
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'pageData' ],
                resolveFn: GetCallFromUSBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmCallFromUS
            }
        }
    },
    {
        name: 'account.activity.details.international-calling',
        url: 'international-calling',
        data: {
            screenName: 'line-international-calling',
            subCategory1: 'my-account'
        },
        params: {
            line_id: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'internationalRate',
                deps: [ XmStore, Transition, CimaCore, 'accountInfo' ],
                resolveFn: GetInternationalRate
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsInternationalCalling
            },
            {
                token: 'showBackToBreadCrumb',
                deps: [ Transition, 'pageData' ],
                resolveFn: ShowBackToBreadCrumb
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ Transition, 'accountSummary', 'showBackToBreadCrumb' ],
                resolveFn: GetActivityDetailBreadcrumb
            }
        ],
        onExit: ClearSession,
        views: {
            'content@account': {
                component: XmInternationalCalling
            }
        }
    },
    {
        name: 'account.activity.details.travel-rate',
        url: 'travel-rate',
        data: {
            screenName: 'addon-travel-rate-search',
            subCategory1: 'my-account'
        },
        params: {
            line_id: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'internationalRate',
                deps: [ XmStore, Transition, CimaCore, 'accountInfo' ],
                resolveFn: GetInternationalRate
            },
            {
                token: 'travelRatesPageData',
                deps: [XmStore],
                resolveFn: GetCmsTravelRates
            },
            {
                token: 'showBackToTravelBreadCrumb',
                deps: [ Transition, 'travelRatesPageData' ],
                resolveFn: ShowBackToTravelBreadCrumb
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ Transition, 'accountSummary', 'showBackToTravelBreadCrumb' ],
                resolveFn: GetActivityDetailBreadcrumb
            }
        ],
        onExit: ClearSession,
        views: {
            'content@account': {
                component: XmTravelRateSearch
            }
        }
    },
    {
        name: 'account.activity.details.travel',
        url: 'travel',
        data: {
            screenName: 'line-travel',
            subCategory1: 'my-account'
        },
        params: {
            line_id: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'addonCollections',
                deps: [ Transition ],
                resolveFn: GetAddonCollections
            },
            {
                token: 'internationalTravelPageData',
                deps: [ XmStore ],
                resolveFn: GetCmsInternationalTravel
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'internationalTravelPageData' ],
                resolveFn: GetInternationalTravelBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmInternationalTravel
            }
        }
    },
    {
        abstract: true,
        name: 'account.activity.addon',
        url: '/addon/:addon_type/:intent',
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: 'accountSummary',
                deps: [ XmStore, Transition, 'accountInfo' ],
                resolveFn: GetAccountSummary
            },
            {
                token: 'checkAddonRoutes',
                deps: [Transition],
                resolveFn: CheckAddonRoutes
            },
            {
                token: 'addonsPlansData',
                deps: [XmStore],
                resolveFn: GetCmsAddonPlans
            }
        ]
    },
    {
        name: 'account.activity.addon.review',
        url: '/review',
        data: {
            screenName: 'addon-review',
            subCategory1: 'my-account'
        },
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [Transition, 'addonsPlansData'],
                resolveFn: GetAddonBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmAddonReview
            }
        }
    },
    {
        name: 'account.activity.addon.review.summary',
        url: '/summary',
        data: {
            screenName: 'addon-summary',
            subCategory1: 'my-account'
        },
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [Transition, 'addonsPlansData'],
                resolveFn: GetAddonBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmAddonSummary
            }
        }
    },
    {
        name: 'account.activity.addon.review.progress',
        url: '/progress',
        data: {
            screenName: 'addon-progress',
            subCategory1: 'my-account'
        },
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [Transition, 'addonsPlansData'],
                resolveFn: GetAddonBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmAddonProgress
            }
        }
    },
    {
        name: 'account.activity.addon.review.pending',
        url: '/pending',
        data: {
            screenName: 'addon-pending',
            subCategory1: 'my-account'
        },
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [Transition, 'addonsPlansData'],
                resolveFn: GetAddonBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmAddonPending
            }
        }
    },
    {
        name: 'account.activity.addon.review.success',
        url: '/success',
        data: {
            screenName: 'addon-success',
            subCategory1: 'my-account'
        },
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [Transition, 'addonsPlansData'],
                resolveFn: GetAddonBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmAddonSuccess
            }
        }
    },
    {
        name: 'account.activity.addon.review.failure',
        url: '/failure',
        data: {
            screenName: 'addon-failure',
            subCategory1: 'my-account'
        },
        params: {
            addon_type: {
                type: ParamType.encoded
            },
            intent: {
                type: ParamType.encoded
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: ['accountInfo', StateService, XmStore, Transition],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [Transition, 'addonsPlansData'],
                resolveFn: GetAddonBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmAddOnFailure
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.review-order',
        url: '',
        data: {
            screenName: 'plan-switch-review-order',
            subCategory1: 'my-account'
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            },
            line_id: {
                type: ParamType.encoded
            },
            backButtonPath: {
                type: ParamType.any
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            }
        ],
        views: {
            'content@account': {
                component: XmPlanSwitchReviewOrder
            }
        }
    },
    {
        name: 'account.activity.details.plan-switch.xmc-consent-page',
        url: '',
        data: {
            screenName: 'plan-switch-xmc-consent-page',
            subCategory1: 'my-account',
            hideBreadcrumb: true
        },
        params: {
            plan_code: {
                type: ParamType.encoded
            },
            line_id: {
                type: ParamType.encoded
            },
            xmcConsent: {
                type: ParamType.any
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetPlanSwitchPageData
            }
        ],
        views: {
            'content@account': {
                component: XmcConsentPageComponent
            }
        }
    },
    {
        name: 'account.activity.details.addon.**',
        url: 'addon',
        loadChildren: () => import('./line-detail/addon/module').then((m: typeof import('./line-detail/addon/module')) => m.AddonModule)
    },
    {
        name: 'account.activity.details.data-saver.**',
        url: 'data-saver',
        loadChildren: () => import('./line-detail/data-saver/module').then((m: typeof import('./line-detail/data-saver/module')) => m.DataSaverModule)
    },
    {
        name: 'account.activity.details.video-resolution.**',
        url: 'video-resolution',
        loadChildren: () => import('./line-detail/video-resolution/module').then((m: typeof import('./line-detail/video-resolution/module')) => m.VideoResolutionModule)
    },
    {
        name: 'account.activity.bucket-switch.**',
        url: '/bucket-switch',
        loadChildren: () => import('./bucket-switch/module').then((m: typeof import('./bucket-switch/module')) => m.BucketSwitchModule)
    },
    {
        name: 'account.activity.switch-locations',
        url: '/switch-locations',
        data: {
            screenName: 'switch-locations',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'switchLocationsAccess',
                deps: [ CimaCore, Transition ],
                resolveFn: CheckSwitchLocationsAccess
            },
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'cbmLocations',
                deps: [ CimaCore, XmStore, Transition ],
                resolveFn: GetCbmLocations
            },
            {
                token: BREADCRUMB_TOKEN,
                resolveFn: GetSwitchLocationsBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmSwitchLocationsComponent
            }
        }
    }
];
